/**
 * Terms and conditions gate on the rental product page.
 *
 * The Rent button belongs to Product Rentals Pro, not to this theme — the
 * theme's own buy buttons are switched off on the rental template. The app
 * renders that button disabled and drives its disabled state itself, turning it
 * on once dates are picked and off again while it loads or when a date is
 * unavailable. So this gate must not touch `disabled`: setting it would either
 * be wiped the next time the app re-evaluates, silently opening the gate, or
 * stick and leave the button dead for good.
 *
 * It intercepts the event instead. The app binds its handler with
 * `form.addEventListener('submit', ...)` — element level, bubble phase — so a
 * listener on `window` in the capture phase runs first and can stop the submit
 * before the app ever sees it. Listening for `submit` rather than the button's
 * click covers pressing Enter inside the form as well as clicking; the click
 * handler below is a second line of defence in case a later version of the app
 * moves to handling clicks directly.
 *
 * Acceptance is deliberately NOT saved to the cart. The client asked for the
 * shopper to accept again in the cart, and the cart runs its own gate
 * (snippets/houghton-cart-terms.liquid) off a cart attribute. This one is
 * page-local state and writes nothing.
 *
 * One limitation worth knowing: the cart gate fails closed, because the server
 * renders the checkout button disabled. This one fails open if the script does
 * not load, because the button it guards is rendered by the app and cannot be
 * given a blocked state on the server.
 */

const FORM = '.rw-form';
const SUBMIT = '.rw-submit';
const TOGGLE = '[data-rental-terms-toggle]';
const HINT = '[data-rental-terms-hint]';

/** @returns {boolean} Whether a gate is on the page at all. */
function gatePresent() {
  return document.querySelector(TOGGLE) !== null;
}

/** @returns {boolean} Whether the shopper has accepted the terms. */
function accepted() {
  const boxes = document.querySelectorAll(TOGGLE);
  if (boxes.length === 0) return true;
  return Array.from(boxes).every((box) => box instanceof HTMLInputElement && box.checked);
}

/**
 * Says why the button did nothing, and puts the shopper on the checkbox.
 *
 * A button that swallows a click without explanation reads as a broken site,
 * and the checkbox may well be off screen on a phone once the calendar is open.
 */
function refuse() {
  document.querySelectorAll(HINT).forEach((hint) => {
    if (hint instanceof HTMLElement) hint.hidden = false;
  });

  const box = document.querySelector(TOGGLE);
  if (!(box instanceof HTMLInputElement)) return;

  const gate = box.closest('rental-terms');
  (gate ?? box).scrollIntoView({ behavior: 'smooth', block: 'center' });
  box.focus({ preventScroll: true });
}

/**
 * Stops a rental being added to the cart until the terms are accepted.
 * @param {Event} event - A submit or click event, caught on the way down.
 */
function guard(event) {
  if (!gatePresent() || accepted()) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const inRentalForm =
    event.type === 'submit' ? target.matches(FORM) : target.closest(SUBMIT) !== null && target.closest(FORM) !== null;

  if (!inRentalForm) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  refuse();
}

class RentalTerms extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onToggle);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onToggle);
  }

  /** @param {Event} event */
  #onToggle = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches(TOGGLE)) return;

    // Ticking the box answers the hint, so take it away. Unticking does not put
    // it back — nothing has been refused yet.
    if (!target.checked) return;

    this.querySelectorAll(HINT).forEach((hint) => {
      if (hint instanceof HTMLElement) hint.hidden = true;
    });
  };
}

// Capture on window is the earliest point in the event's path, which is what
// puts this ahead of the handler the app bound on the form itself.
if (!window.houghtonRentalTermsBound) {
  window.houghtonRentalTermsBound = true;
  window.addEventListener('submit', guard, true);
  window.addEventListener('click', guard, true);
}

if (!customElements.get('rental-terms')) {
  customElements.define('rental-terms', RentalTerms);
}
